'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Button } from '@ironhack/design-system2/components/button';
import { Flex } from '@ironhack/design-system2/components/flex';
import { Text } from '@ironhack/design-system2/components/text';
import { ChevronLeft } from '@ironhack/design-system2/icons/chevron-left';
import NextLink from 'next/link';
import { type ReactElement } from 'react';
import { Image, type StructuredTextGraphQlResponse } from 'react-datocms';

import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { StructuredContent } from '@/app/components/client/structured-content';
import { getPageUrl } from '@/app/urls/url-mapping';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { updateBlogHomeRegion } from '@/lib/utils/blog';
import { type ModuleData } from '../../blog-author.queries';

type Props = ModuleData & BoxProps;

export const Header = (props: Props): ReactElement => {
  const { author, blogAuthorPage, ...boxProps } = props;

  const {
    header: [header],
  } = blogAuthorPage;

  const { params } = usePageContext();
  const blogHomeParams = updateBlogHomeRegion(params);

  return (
    <ModuleWrapper
      innerProps={{
        align: 'center',
        direction: 'column',
      }}
      moduleFormat={{
        spacingBottom: '0',
        spacingTop: '0',
        background: 'transparent',
      }}
      outerProps={{ as: 'header', ...boxProps }}
    >
      <Button
        _hover={{
          color: 'link.inverse',
          textDecoration: 'underline',
        }}
        alignSelf="start"
        as={NextLink}
        color="link.inverse"
        fontSize="s"
        href={getPageUrl('blog', blogHomeParams) ?? '#/'}
        leftIcon={ChevronLeft}
        my={3}
        size="s"
        variant="tertiary"
      >
        {header.backButtonText}
      </Button>
      <Flex
        direction={['column', null, 'row']}
        gap={[null, null, 3, 4]}
        w="full"
      >
        <Flex
          color="text.inverse"
          direction="column"
          mb={[4, null, 8, 12]}
          mt={[2, null, 8, 12]}
          width={['100%', null, '50%', '480px']}
        >
          <Text as="h1" textStyle={['3xl', null, '5xl']}>
            {author.name}
          </Text>
          <Text mt={[2, null, 1]} textStyle="lBold" textTransform="uppercase">
            {author.jobTitle}
          </Text>
          <StructuredContent
            color="text.inverse"
            data={author.bio as StructuredTextGraphQlResponse}
            ignoreLinkColor={false}
            mt={2}
          />
        </Flex>
        <Flex align="end" flex="1" justify="center">
          {author.photo && <Image data={author.photo.responsiveImage} />}
        </Flex>
      </Flex>
    </ModuleWrapper>
  );
};
